/**
 * When we meet, and what to expect on a first Sunday.
 *
 * Two gaps the church finder kept pointing at. Not one of the 45 church
 * records had a service time on it — the registry had grown a free-text field
 * nothing wrote to, and a dated register of services held, which is for
 * marking attendance rather than telling a stranger when to turn up. And
 * nothing anywhere said what happens when you arrive: whether to dress up,
 * where to park, how long it lasts, what happens with the children.
 *
 * Both go to the two people who most need them: the stranger reading the
 * finder on a phone, and the applicant who has just pressed send and has never
 * seen the building.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Plus, Trash2, Save, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api, { getErrorMessage } from '@/lib/api';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday',
              'Saturday', 'Every day'];

export const ChurchVisitDetails = ({ churchId }) => {
  const qc = useQueryClient();
  const [rows, setRows] = useState([]);
  const [expect, setExpect] = useState('');
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['church-visit-details', churchId],
    queryFn: () => api.get(`/admin/churches/${churchId}/visit-details`).then((r) => r.data),
    enabled: !!churchId,
  });

  useEffect(() => {
    if (!data || dirty) return;
    // A church that typed its times into the old free-text field keeps them:
    // they appear as a first row rather than being quietly dropped.
    const existing = data.weekly_services?.length
      ? data.weekly_services
      : data.legacy_service_times
        ? [{ day: 'Sunday', time: '', name: data.legacy_service_times, note: '' }]
        : [];
    setRows(existing);
    setExpect(data.what_to_expect || '');
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () => api.put(`/admin/churches/${churchId}/visit-details`, {
      weekly_services: rows.filter((r) => (r.time || '').trim() || (r.name || '').trim()),
      what_to_expect: expect,
    }),
    onSuccess: () => {
      toast.success('Saved — visitors now see this');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['church-visit-details', churchId] });
      qc.invalidateQueries({ queryKey: ['church-listing', churchId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not save')),
  });

  const edit = (i, patch) => {
    setRows((list) => list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  if (isLoading) return null;

  return (
    <Card data-testid="church-visit-details">
      <CardContent className="space-y-4 p-4">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
            <Clock className="h-4 w-4 text-slate-400" />When we meet
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Shown in the church finder, on the welcome page a QR code opens, and in
            the email a new applicant receives.
          </p>
        </div>

        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-slate-200 p-2 sm:flex sm:items-center sm:gap-2 sm:space-y-0 sm:border-0 sm:p-0">
              <Select value={row.day || 'Sunday'} onValueChange={(v) => edit(i, { day: v })}>
                <SelectTrigger className="w-full sm:w-36" data-testid={`service-day-${i}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                value={row.time || ''}
                onChange={(e) => edit(i, { time: e.target.value })}
                placeholder="9:00am"
                className="w-full sm:w-32"
                data-testid={`service-time-${i}`}
              />
              <Input
                value={row.name || ''}
                onChange={(e) => edit(i, { name: e.target.value })}
                placeholder="Morning Service"
                className="w-full min-w-0 sm:flex-1"
                data-testid={`service-name-${i}`}
              />
              <Button
                type="button" variant="ghost"
                className="min-h-[44px] w-full text-rose-600 sm:w-auto"
                data-testid={`service-remove-${i}`}
                onClick={() => { setRows((l) => l.filter((_, j) => j !== i)); setDirty(true); }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline" size="sm" className="min-h-[44px]"
            data-testid="service-add"
            onClick={() => {
              setRows((l) => [...l, { day: 'Sunday', time: '', name: '', note: '' }]);
              setDirty(true);
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />Add a service
          </Button>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <Label className="flex items-center gap-1.5 text-xs">
            <Info className="h-3.5 w-3.5" />What to expect on a first visit
          </Label>
          <p className="mb-1.5 mt-0.5 text-xs text-slate-500">
            Parking, how long the service lasts, what people wear, what happens with
            children. Left blank, this paragraph simply does not appear — nothing
            generic is written on your behalf.
          </p>
          <Textarea
            rows={4}
            value={expect}
            onChange={(e) => { setExpect(e.target.value); setDirty(true); }}
            placeholder="Park in the school yard next door. The service lasts about two hours. Come as you are. Children stay with us for the first twenty minutes, then go to their own groups."
            data-testid="visit-what-to-expect"
          />
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending || !dirty}
                  data-testid="visit-details-save">
            {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            : <Save className="mr-1.5 h-4 w-4" />}
            Save
          </Button>
          {dirty ? (
            <span className="text-xs text-amber-700" data-testid="visit-details-dirty">
              Not saved yet
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChurchVisitDetails;
